import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { LayoutSingleColumn, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import LandingFooter from './Components/LandingFooter/LandingFooter';
import css from './LandingPage.module.css';
import loadable from '@loadable/component';
import { useConfiguration } from '../../context/configurationContext';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import getDataNotificationBar from '../../util/getDataNotificationBar';
import IconClose2 from './IconClose2/IconClose2';
import { useIntl } from '../../util/reactIntl';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { getLandingPageSettingsApi } from '../../util/api';
import { userLocationWithAddress } from '../../util/maps';
import SectionTabs from './Components/SectionTabs/SectionTabs';
import { useTheme } from '../../context/themeContext';

// The notification bar uses the marketplace gold rather than the appearance
// colors configured in the Console.
const NOTIFICATION_BAR_BACKGROUND_COLOR = '#d19c50';
const NOTIFICATION_BAR_TEXT_COLOR = '#000000';

// Components - lazy loaded
const HeroSection = loadable(() => import('./Components/Hero/HeroSection'), { ssr: false });
const CategoryGrid = loadable(() => import('./Components/CategoryGrid/CategoryGrid'), {
  ssr: false,
});
const FeaturedCoaches = loadable(() => import('./Components/FeaturedCoaches/FeaturedCoaches'), {
  ssr: false,
});
const FeaturedSubscriptions = loadable(
  () => import('./Components/FeaturedSubscriptions/FeaturedSubscriptions'),
  { ssr: false }
);
const MarketplaceSection = loadable(
  () => import('./Components/MarketplaceSection/MarketplaceSection'),
  { ssr: false }
);
const LevelingSection = loadable(
  () => import('./Components/LevelingSection/LevelingSection'),
  { ssr: false }
);
const AltitudesRail = loadable(
  () => import('./Components/AltitudesRail/AltitudesRail'),
  { ssr: false }
);
const BecomeACoachSection = loadable(
  () => import('./Components/BecomeACoach/BecomeACoachSection'),
  { ssr: false }
);
const FeaturedCoachSessions = loadable(
  () => import('./Components/FeaturedCoachesSession/FeaturedCoachSessions'),
  { ssr: false }
);

const CampSessions = loadable(() => import('./Components/CampSession/CampSessions'), {
  ssr: false,
});

const LANDING_PAGE_SECTIONS = [
  { id: 'featured-coaches', label: 'The Coaches', icon: 'users' },
  { id: 'marketplace', label: 'Marketplace', icon: 'map' },
  { id: 'featured-subscriptions', label: 'Subscriptions', icon: 'users' },
  { id: 'become-coach', label: 'For Coaches', icon: 'compass' },
];

function LandingPage() {
  const intl = useIntl();
  const [activeSection, setActiveSection] = useState(
    LANDING_PAGE_SECTIONS[0]?.id || 'featured-coaches'
  );

  useEffect(() => {
    const handleScroll = () => {
      const offset = 150;
      for (const section of LANDING_PAGE_SECTIONS) {
        const element = document.getElementById(`section-${section.id}`);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= offset && rect.bottom > offset) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const [activeFilters] = useState(() => ({}));

  // Light is the landing page's default look; dark (the ink theme) is an
  // opt-in a visitor can switch to via the ThemeToggle button (now rendered
  // in the sitewide Topbar, next to the Maps link -- see
  // TopbarDesktop.js/TopbarMobileMenu.js), remembered for next time. Theme
  // state itself lives in ThemeContext (src/context/themeContext.js) so the
  // Topbar and this page share the same value; the CSS it drives is still
  // scoped entirely to this page's own --cfy-* tokens (see
  // LandingPage.module.css's .landingLayout) -- never touches the coach
  // profile page's own always-dark hero band or anything outside here.
  const { theme } = useTheme();

  const landingHeaderRef = useRef(null);
  const [landingHeaderHeight, setLandingHeaderHeight] = useState(116);

  const [settings, setSettings] = useState({
    locations: [],
    featuredCoachIds: [],
  });
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [settingsError, setSettingsError] = useState(null);

  const [currentLocation, setCurrentLocation] = useState(null);

  const config = useConfiguration();
  const history = useHistory();
  const scrollingDisabled = useSelector(isScrollingDisabled);

  const loadSettings = useCallback(async () => {
    try {
      setIsLoadingSettings(true);
      setSettingsError(null);
      const response = await getLandingPageSettingsApi();
      console.log('[LandingPage] Settings response:', response);
      if (response?.data) {
        const { locations = [], featuredCoachIds = [] } = response.data;
        console.log('[LandingPage] Loaded locations:', locations);
        setSettings({
          locations: locations || [],
          featuredCoachIds: featuredCoachIds || [],
        });
      }
    } catch (err) {
      console.error('Error loading landing page settings:', err);
      setSettingsError('Failed to load settings');
      // Set empty settings on error
      setSettings({ locations: [], featuredCoachIds: [] });
    } finally {
      setIsLoadingSettings(false);
    }
  }, []);

  const loadUserLocation = useCallback(async () => {
    const hasSavedLocations = settings.locations && settings.locations.length > 0;

    // Check if site is served over HTTPS (required for geolocation in modern browsers)
    const isSecureContext =
      window.isSecureContext ||
      window.location.protocol === 'https:' ||
      window.location.hostname === 'localhost';

    if (!isSecureContext) {
      console.log('Site not served over HTTPS, skipping geolocation');

      if (hasSavedLocations) {
        const firstLoc = settings.locations[0];
        setCurrentLocation({
          lat: firstLoc.lat,
          lng: firstLoc.lng,
          address: firstLoc.address || 'Selected Location',
        });
      } else {
        setCurrentLocation({
          lat: 34.0522,
          lng: -118.2437,
          address: 'Los Angeles, California, USA',
          placeName: 'Los Angeles',
        });
      }
      return;
    }

    try {
      // Get user's location with reverse geocoded address
      const location = await userLocationWithAddress();

      setCurrentLocation({
        lat: location.lat,
        lng: location.lng,
        address: location.address || 'Your Location',
        placeName: location.placeName || 'Your Location',
      });
    } catch (error) {
      console.log('Geolocation not available or denied:', error.message || error);

      // Fallback location for map center only
      if (hasSavedLocations) {
        const firstLoc = settings.locations[0];
        setCurrentLocation({
          lat: firstLoc.lat,
          lng: firstLoc.lng,
          address: firstLoc.address || 'Selected Location',
        });
      } else {
        // Default to Los Angeles for map center
        setCurrentLocation({
          lat: 34.0522,
          lng: -118.2437,
          address: 'Los Angeles, California, USA',
          placeName: 'Los Angeles',
        });
      }
      // Don't set neighborhoodLocation when geolocation is denied
    }
  }, [settings.locations]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (config.maps.mapboxAccessToken && window.mapboxgl) {
      window.mapboxgl.accessToken = config.maps.mapboxAccessToken;
    }
  }, [config.maps.mapboxAccessToken]);

  useEffect(() => {
    if (!isLoadingSettings) {
      loadUserLocation();
    }
  }, [isLoadingSettings, loadUserLocation]);

  useEffect(() => {
    const hasVisited = localStorage.getItem('coachify_visited');
    if (!hasVisited) {
      setIsVideoModalOpen(true);
    }
  }, []);

  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const handleCloseVideoModal = () => {
    setIsVideoModalOpen(false);
    localStorage.setItem('coachify_visited', 'true');
  };

  const {
    isDisplayNotificationBarFromFlexConsole,
    contentTitle,
    contentCallToAction,
    hrefCallToAction,
  } = getDataNotificationBar(config);
  const [isNotificationOpen, setIsNotificationOpen] = useState(true);

  const videoUrl = intl.formatMessage({ id: 'LandingPage.videoUrl' });
  const hasVideo = videoUrl?.trim() !== 'LandingPage.videoUrl';

  const showNotificationBar = isNotificationOpen && isDisplayNotificationBarFromFlexConsole;

  const measureLandingHeaderHeight = useCallback(() => {
    const node = landingHeaderRef.current;
    if (!node) {
      return;
    }
    const height = Math.ceil(node.getBoundingClientRect().height);
    if (height > 0) {
      setLandingHeaderHeight(prev => (prev !== height ? height : prev));
    }
  }, []);

  useLayoutEffect(() => {
    measureLandingHeaderHeight();
  }, [measureLandingHeaderHeight, showNotificationBar]);

  useEffect(() => {
    const node = landingHeaderRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      measureLandingHeaderHeight();
    });
    observer.observe(node);

    window.addEventListener('resize', measureLandingHeaderHeight);
    window.addEventListener('scroll', measureLandingHeaderHeight, { passive: true });

    // Topbar is loadable; re-measure after async chunk paints.
    const delayedMeasure = window.setTimeout(measureLandingHeaderHeight, 250);
    const delayedMeasure2 = window.setTimeout(measureLandingHeaderHeight, 1000);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measureLandingHeaderHeight);
      window.removeEventListener('scroll', measureLandingHeaderHeight);
      window.clearTimeout(delayedMeasure);
      window.clearTimeout(delayedMeasure2);
    };
  }, [measureLandingHeaderHeight, showNotificationBar]);

  return (
    <Page
      title={intl.formatMessage({ id: 'LandingPage.title' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn
        rootClassName={css.landingLayout}
        data-cfy-theme={theme}
        style={{
          '--landing-sticky-header-height': `${landingHeaderHeight}px`,
        }}
        topbar={
          <div ref={landingHeaderRef} className={css.landingHeaderStack}>
            {showNotificationBar && (
              <div
                className={css.notificationBar}
                style={{
                  background: NOTIFICATION_BAR_BACKGROUND_COLOR,
                  color: NOTIFICATION_BAR_TEXT_COLOR,
                }}
              >
                <div>
                  {contentTitle} &nbsp;
                  <a
                    className={css.linkNotification}
                    style={{ color: NOTIFICATION_BAR_TEXT_COLOR }}
                    href={hrefCallToAction}
                  >
                    {contentCallToAction}
                  </a>
                </div>
                <div className={css.iconCloseWrapper}>
                  <IconClose2
                    style={{
                      fill: NOTIFICATION_BAR_TEXT_COLOR,
                      stroke: NOTIFICATION_BAR_TEXT_COLOR,
                    }}
                    className={css.iconClose}
                    onClick={() => setIsNotificationOpen(false)}
                  />
                </div>
              </div>
            )}
            <TopbarContainer
              desktopClassName={css.desktopTopbar}
              mobileClassName={css.mobileTopbar}
            />
          </div>
        }
        footer={<LandingFooter />}
        currentPage="LandingPage"
      >
        <div className={css.content}>
          <div className={css.layout}>
            <AltitudesRail />
            <div className={css.contentCol}>
              <HeroSection currentLocation={currentLocation} />

              {/* Sticky Navigation Tabs */}
              <div
                className={css.stickyTabsContainer}
                style={{ top: `${landingHeaderHeight}px` }}
              >
                <div className={css.stickyTabsInner}>
                  <SectionTabs
                    sections={LANDING_PAGE_SECTIONS}
                    activeSection={activeSection}
                    onSectionClick={setActiveSection}
                  />
                </div>
              </div>

              {/* <CategoryGrid onCategorySelect={onCategorySelect} /> */}

              <div id="section-featured-coaches" className={css.resultsSection}>
                <FeaturedCoaches
                  title="Lit up this week"
                  featuredCoachIds={settings.featuredCoachIds}
                  filters={activeFilters}
                  currentLocation={currentLocation}
                  isSettingsReady={!isLoadingSettings}
                />
              </div>

              <div id="section-marketplace">
                <MarketplaceSection />
              </div>

              <LevelingSection />

              <div id="section-featured-subscriptions">
                <FeaturedSubscriptions filters={activeFilters} />
              </div>

              <div id="section-become-coach">
                <BecomeACoachSection />
              </div>
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
}

export default LandingPage;
